import { toast, types, customerPortal } from "@vilocnv/allsetra-core";
import {
  selectDrawerSelectedAccountId,
  selectQueriedObjectsState,
  selectReportsState
} from "app/data/selectors";
import { getHydratedMapPreferences } from "app/data/helpers/mapPreferences.helpers";
import {
  resetSideMenuObjects,
  setActiveObjectId,
  setFilters
} from "app/features";
import { ObjectsLocationsRequestDto } from "domain/objects/entities/dtos/objects-locations-request.dto";
import getObjectDataForMapAction from "domain/objects/store/actions/get-object-data-for-map.action";
import getObjectsLocationsAction from "domain/objects/store/actions/get-objects-locations.action";
import {
  clearSelectedObject,
  setSelectedObject,
  setObjectFilters,
  setShowInactiveObjects
} from "domain/objects/store/objects.slice";
import { selectObjectsState } from "domain/objects/store/selectors/objects.selector";
import { selectGeozonesState } from "domain/geozones/store/selectors/geozones.selector";
import {
  useAppDispatch,
  useAppSelector,
  useObjectsResolvedAddressess
} from "hooks";
import useMapPreferencesPersistence from "hooks/useMapPreferencesPersistence";
import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLastTripsPanelController } from "../../hooks/useLastTripsPanelController";
import { useSearchParams } from "react-router-dom";
import { getGeozonesAction } from "domain/geozones/store/actions/get-geozones.action";
import { SignalRService } from "app/data/services";
import onObjectLocationReportedEventAction from "domain/objects/store/actions/on-object-location-reported-event.action";
import onObjectIconUpdatedEventAction from "domain/objects/store/actions/on-object-icon-updated-event.action";
import { selectGroupsState } from "domain/groups/store/selectors/groups.selector";
import { selectObjectTypesState } from "domain/object-types/store/selectors/object-types.selector";
import { selectKeysState } from "domain/keys/store/selectors/keys.selector";
import { getKeysAction } from "domain/keys/store/actions/get-keys.action";
import { getGroupsAction } from "domain/groups/store/actions/get-groups.action";
import { getObjectTypesAction } from "domain/object-types/store/actions/get-object-types.action";
import { FormikHelpers } from "formik";
import {
  extractValidMapPositions,
  fitMapToVisibleMarkers,
  focusMapOnSingleVehicle,
  getAddressDisplayInfo,
  getResolvedAddressFromStore,
  isValidMapCoordinate,
  resetMapToOverview
} from "app/data/helpers";
import { MapViewPreferences } from "app/data/types/mapPreferences.types";
import getObjectsAction from "domain/objects/store/actions/get-objects.actions";
import { ObjectsMapFilterFormValues } from "components/forms/objects/ObjectsMapFilterForm";
import useFitMapToVisibleUnits from "hooks/useFitMapToVisibleUnits";
import { DEFAULT_COORDINATES } from "app/data/constants";
import {
  DEFAULT_SIDE_PANEL_WIDTH,
  MAX_SIDE_PANEL_WIDTH,
  MIN_SIDE_PANEL_WIDTH
} from "./Map.styled";

type ObjectForMapRequestDto = customerPortal.objects.ObjectForMapRequestDto;
type GetGeozonesRequestDto = customerPortal.geozones.GetGeozonesRequestDto;
type MapToggles = Pick<
  MapViewPreferences,
  | "showSatelliteMode"
  | "showClusters"
  | "showTraffic"
  | "showGeozones"
  | "autoFitVisibleUnits"
>;

const useDashboardMapPage = () => {
  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const [mapRef, setMapRef] = useState<google.maps.Map>();
  const [showClusters, setShowClusters] = useState(true);
  const [showSatelliteMode, setShowSatelliteMode] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showGeozones, setShowGeozones] = useState(true);
  const [autoFitVisibleUnits, setAutoFitVisibleUnits] = useState(false);
  const [selectedTooltipMarker, setSelectedTooltipMarker] =
    useState<types.MarkerType>();
  const [selectedCluster, setSelectedCluster] = useState<any>();
  const [mounted, setMounted] = useState(false);
  const [modalFilterOpen, setModalFilterOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false);
  const [sidePanelWidth, setSidePanelWidth] = useState(
    DEFAULT_SIDE_PANEL_WIDTH
  );
  const [isResizingSidePanel, setIsResizingSidePanel] = useState(false);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";

  const { geozones } = useAppSelector(selectGeozonesState);
  const { groups, isLoadingGroups } = useAppSelector(selectGroupsState);
  const { objectTypes, isLoadingObjectTypes } = useAppSelector(
    selectObjectTypesState
  );
  const { keys, isLoadingKeys } = useAppSelector(selectKeysState);
  const {
    objects,
    markers,
    isLoadingObjects,
    isLoadingObjectDetail,
    objectFilters,
    selectedObject
  } = useAppSelector(selectObjectsState);
  const { sideMenuObjects, sideMenuObjectsLoading, totalSideMenuRecords } =
    useAppSelector(selectQueriedObjectsState);
  const {
    resolvedAddresses: sideMenuResolvedAddresses,
    resolvedAddressLoading: sideMenuResolvedAddressLoading
  } = useObjectsResolvedAddressess(sideMenuObjects);
  const { resolvedAddresses } = useAppSelector(selectReportsState);
  const {
    lastTripsPanelOpen,
    lastTrips,
    lastTripsLoading,
    lastTripsError,
    selectedTripId,
    selectedTrip,
    rideLocations,
    locationsLoading,
    locationsError,
    handleOpenLastTripsPanel,
    handleCloseLastTripsPanel,
    handleSelectLastTrip,
    lastTripsObjectName
  } = useLastTripsPanelController(
    accountId ?? undefined,
    selectedObject?.uniqueId
  );

  const displayedMarkers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return markers;
    }

    const sideMenuIds = new Set(
      (sideMenuObjects || []).map((o: any) => o.uniqueId).filter(Boolean)
    );

    return markers.filter(
      (marker) =>
        sideMenuIds.has(marker.uniqueId) ||
        marker.name?.toLowerCase().includes(query) ||
        marker.uniqueId?.toLowerCase().includes(query)
    );
  }, [searchQuery, markers, sideMenuObjects]);

  const { mergeTogglesSync, mergeFiltersSync, getCachedSync, canPersist } =
    useMapPreferencesPersistence();

  const [showInactiveObjects, setShowInactiveObjectsLocal] = useState(false);

  const togglesRef = useRef<MapToggles>({
    showSatelliteMode: false,
    showClusters: true,
    showTraffic: false,
    showGeozones: true,
    autoFitVisibleUnits: false
  });
  const preferencesHydratedForAccountRef = useRef<string | null>(null);
  const pendingFiltersRef = useRef<types.IMapFilter | null>(null);

  useEffect(() => {
    setMounted(true);

    return () => {
      dispatch(setActiveObjectId(null));
      dispatch(resetSideMenuObjects());
    };
  }, [dispatch]);

  useLayoutEffect(() => {
    if (!accountId) {
      preferencesHydratedForAccountRef.current = null;
      pendingFiltersRef.current = null;
      return;
    }

    if (!canPersist) return;

    const {
      toggles,
      filters,
      showInactiveObjects: inactivePref
    } = getHydratedMapPreferences(getCachedSync());

    setShowSatelliteMode(toggles.showSatelliteMode);
    setShowClusters(toggles.showClusters);
    setShowTraffic(toggles.showTraffic);
    setShowGeozones(toggles.showGeozones);
    setAutoFitVisibleUnits(toggles.autoFitVisibleUnits);
    togglesRef.current = toggles;

    setShowInactiveObjectsLocal(inactivePref);
    dispatch(setShowInactiveObjects(inactivePref));

    dispatch(setObjectFilters(filters));
    dispatch(setFilters(filters));
    pendingFiltersRef.current = filters;
    preferencesHydratedForAccountRef.current = accountId;
  }, [accountId, canPersist, getCachedSync, dispatch]);

  togglesRef.current = {
    showSatelliteMode,
    showClusters,
    showTraffic,
    showGeozones,
    autoFitVisibleUnits
  };

  const persistToggles = useCallback(
    (toggles: MapToggles) => {
      if (!canPersist) return;
      togglesRef.current = toggles;
      mergeTogglesSync(toggles);
    },
    [canPersist, mergeTogglesSync]
  );

  useEffect(() => {
    if (!mounted) return;

    if (!accountId) {
      toast.info("Kindly select an account from the navigation drawer.");
      return;
    }

    if (canPersist && preferencesHydratedForAccountRef.current !== accountId) {
      return;
    }

    SignalRService.hubConnection?.on("EventRaised", liveUpdateObject);

    const filters = pendingFiltersRef.current ?? objectFilters;
    pendingFiltersRef.current = null;

    dispatchObjects(filters);
    dispatch(
      getObjectsAction({
        accountId: accountId ?? "",
        filters: filters ?? { groups: [], objectTypes: [], keyId: [] }
      })
    );
    dispatchGeozones();
    dispatchGroups();
    dispatchKeys();
    dispatchObjectTypes();

    return () => {
      SignalRService.hubConnection?.off("EventRaised", liveUpdateObject);
    };
  }, [mounted, accountId, objectFilters, canPersist]);

  const liveUpdateObject = useCallback(
    (event: any) => {
      if (modalFilterOpen || isSelectOpen) return;

      if (selectedObject) {
        const latitude = selectedObject?.location?.latitude;
        const longitude = selectedObject?.location?.longitude;
        const objectResolvedAddress = selectedObject?.location?.resolvedAddress;

        const finalResolvedAddress = getResolvedAddressFromStore(
          objectResolvedAddress,
          resolvedAddresses || [],
          latitude,
          longitude
        );

        const addressInfo = getAddressDisplayInfo(
          latitude,
          longitude,
          finalResolvedAddress
        );

        if (addressInfo.isHidden) return;
      }

      switch (event?.eventName) {
        case types.BackendEventsEnum.ObjectLocationReportedEvent:
          dispatch(
            onObjectLocationReportedEventAction({
              objectLocationEventData: event,
              accountId
            })
          );
          break;
        case types.BackendEventsEnum.ObjectIconUpdatedEvent:
          dispatch(
            onObjectIconUpdatedEventAction({
              objectIconUpdatedEventData: event
            })
          );
          break;

        default:
          break;
      }
    },
    [
      dispatch,
      accountId,
      modalFilterOpen,
      isSelectOpen,
      selectedObject,
      resolvedAddresses
    ]
  );

  const dispatchObjects = async (filters: types.IMapFilter) => {
    const requestData: ObjectsLocationsRequestDto = {
      accountId: accountId ?? "",
      filters: {
        ...filters
      }
    };

    return dispatch(getObjectsLocationsAction(requestData));
  };

  const dispatchGeozones = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getGeozonesAction(requestData));
  };

  const dispatchKeys = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getKeysAction(requestData));
  };

  const dispatchGroups = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getGroupsAction(requestData));
  };

  const dispatchObjectTypes = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getObjectTypesAction(requestData));
  };

  useFitMapToVisibleUnits(
    mapRef,
    displayedMarkers,
    autoFitVisibleUnits,
    accountId,
    selectedObject?.uniqueId
  );

  const handleAutoFitVisibleUnitsButtonClick = () => {
    const next = !autoFitVisibleUnits;
    setAutoFitVisibleUnits(next);
    persistToggles({
      showSatelliteMode,
      showClusters,
      showTraffic,
      showGeozones,
      autoFitVisibleUnits: next
    });

    if (next) {
      fitMapToVisibleMarkers(
        mapRef,
        extractValidMapPositions(displayedMarkers)
      );
    } else {
      resetMapToOverview(mapRef, DEFAULT_COORDINATES);
    }
  };

  const findObjectById = useCallback(
    (uniqueId: string): types.IObject | undefined => {
      return (
        objects.find((obj) => obj.uniqueId === uniqueId) ??
        sideMenuObjects?.find((obj: types.IObject) => obj.uniqueId === uniqueId)
      );
    },
    [objects, sideMenuObjects]
  );

  const focusOnVehicle = (uniqueId: string) => {
    const object = findObjectById(uniqueId);
    const location = object?.location;

    if (
      location &&
      isValidMapCoordinate(location.latitude, location.longitude)
    ) {
      focusMapOnSingleVehicle(mapRef, {
        lat: location.latitude,
        lng: location.longitude
      });
      return;
    }

    const marker = displayedMarkers.find((m) => m.uniqueId === uniqueId);
    const [position] = extractValidMapPositions(marker ? [marker] : []);
    focusMapOnSingleVehicle(mapRef, position);
  };

  const handleMapLoad = (map: google.maps.Map) => {
    if (map) {
      setMapRef(map);
    }
  };

  const handleMarkerMouseOver = (uniqueId: string) => {
    setSelectedTooltipMarker(undefined);
    const marker = displayedMarkers.find((m) => m?.uniqueId === uniqueId);
    if (marker && marker.position && marker.name) {
      setSelectedTooltipMarker(marker);
    }
  };

  const handleMarkerMouseOut = () => {
    setSelectedTooltipMarker(undefined);
  };

  const handleShowInfoWindowClick = (uniqueId: string) => {
    if (!uniqueId) return;

    const fallbackObject = findObjectById(uniqueId);
    focusOnVehicle(uniqueId);
    dispatch(setActiveObjectId(uniqueId));
    dispatch(setSelectedObject({ uniqueId, fallbackObject }));

    const requestData: ObjectForMapRequestDto = {
      accountId: accountId ?? "",
      objectId: uniqueId
    };
    dispatch(getObjectDataForMapAction(requestData));
  };

  const handleCloseInfoWindowClick = () => {
    dispatch(clearSelectedObject());
    dispatch(setActiveObjectId(null));
  };

  const handleClusterClick = (cluster: any) => {
    setSelectedCluster(cluster);
  };

  const handleClusterCloseInfoWindowClick = () => {
    setSelectedCluster(undefined);
  };

  const handleShowSatelliteButtonClick = () => {
    const next = !showSatelliteMode;
    setShowSatelliteMode(next);
    persistToggles({
      showSatelliteMode: next,
      showClusters,
      showTraffic,
      showGeozones,
      autoFitVisibleUnits
    });
  };

  const handleShowClustersButtonClick = () => {
    const next = !showClusters;
    setShowClusters(next);
    persistToggles({
      showSatelliteMode,
      showClusters: next,
      showTraffic,
      showGeozones,
      autoFitVisibleUnits
    });
  };

  const handleShowTrafficButtonClick = () => {
    const next = !showTraffic;
    setShowTraffic(next);
    persistToggles({
      showSatelliteMode,
      showClusters,
      showTraffic: next,
      showGeozones,
      autoFitVisibleUnits
    });
  };

  const handleShowGeozonesButtonClick = () => {
    const next = !showGeozones;
    setShowGeozones(next);
    persistToggles({
      showSatelliteMode,
      showClusters,
      showTraffic,
      showGeozones: next,
      autoFitVisibleUnits
    });
  };

  const handleFilterModalCloseClick = () => {
    setModalFilterOpen(!modalFilterOpen);
  };

  const handleFilterModalOpenClick = () => {
    setModalFilterOpen(!modalFilterOpen);
  };

  const handleModalFilterFormSubmit = async (
    values: ObjectsMapFilterFormValues,
    formikHelpers: FormikHelpers<ObjectsMapFilterFormValues>
  ) => {
    formikHelpers.setSubmitting(true);

    const { showInactiveObjects: nextShowInactive, ...filterValues } = values;

    const { type } = await dispatchObjects(filterValues);

    if (type === "objects/v2/getObjectsLocation/fulfilled") {
      formikHelpers.setSubmitting(false);
      setModalFilterOpen(false);
      dispatch(setObjectFilters(filterValues));
      setShowInactiveObjectsLocal(nextShowInactive);
      dispatch(setShowInactiveObjects(nextShowInactive));

      if (canPersist) {
        mergeFiltersSync(filterValues);
        mergeTogglesSync({ showInactiveObjects: nextShowInactive });
      }
    }
  };

  const handleFilterChange = (value: string) => {
    if (!value) return;

    const object = findObjectById(value);
    if (!object) return;

    handleShowInfoWindowClick(value);
  };

  const handleToggleSidePanelCollapse = useCallback(() => {
    setIsSidePanelCollapsed((prev) => !prev);
  }, []);

  const handleSidePanelResizeMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = sidePanelWidth;
      setIsResizingSidePanel(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const nextWidth = Math.min(
          MAX_SIDE_PANEL_WIDTH,
          Math.max(MIN_SIDE_PANEL_WIDTH, startWidth + deltaX)
        );
        setSidePanelWidth(nextWidth);
      };

      const handleMouseUp = () => {
        setIsResizingSidePanel(false);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [sidePanelWidth]
  );

  const handleSidePanelResizeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setSidePanelWidth((prev) => Math.max(MIN_SIDE_PANEL_WIDTH, prev - 20));
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        setSidePanelWidth((prev) => Math.min(MAX_SIDE_PANEL_WIDTH, prev + 20));
      }
    },
    []
  );

  const combinedResolvedAddresses = useMemo(
    () => [...(resolvedAddresses || []), ...(sideMenuResolvedAddresses || [])],
    [resolvedAddresses, sideMenuResolvedAddresses]
  );

  const addressInfo = selectedObject
    ? getAddressDisplayInfo(
        selectedObject?.location?.latitude,
        selectedObject?.location?.longitude,
        getResolvedAddressFromStore(
          selectedObject?.location?.resolvedAddress,
          combinedResolvedAddresses,
          selectedObject?.location?.latitude,
          selectedObject?.location?.longitude
        )
      )
    : undefined;

  return {
    mapRef,
    accountId: accountId ?? "",
    objectFilters,
    markers: displayedMarkers,
    showClusters,
    showSatelliteMode,
    showTraffic,
    geozones,
    showGeozones,
    showInactiveObjects,
    autoFitVisibleUnits,
    groups,
    isLoadingGroups,
    keys,
    isLoadingKeys,
    objectTypes,
    isLoadingObjectTypes,
    isLoadingObjects,
    isLoadingObjectDetail,
    selectedTooltipMarker,
    selectedObject,
    selectedCluster,
    modalFilterOpen,
    isSelectOpen,
    addressInfo,
    lastTripsPanelOpen,
    lastTrips,
    lastTripsLoading,
    lastTripsError,
    selectedTripId,
    selectedTrip,
    rideLocations,
    locationsLoading,
    locationsError,
    sideMenuObjects,
    sideMenuObjectsLoading,
    totalSideMenuRecords,
    sideMenuResolvedAddresses,
    sideMenuResolvedAddressLoading,
    isSidePanelCollapsed,
    sidePanelWidth,
    isResizingSidePanel,
    setIsSelectOpen,
    handleMapLoad,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleShowInfoWindowClick,
    handleCloseInfoWindowClick,
    handleClusterClick,
    handleClusterCloseInfoWindowClick,
    handleShowSatelliteButtonClick,
    handleShowClustersButtonClick,
    handleShowTrafficButtonClick,
    handleShowGeozonesButtonClick,
    handleFilterModalCloseClick,
    handleFilterModalOpenClick,
    handleModalFilterFormSubmit,
    handleFilterChange,
    handleOpenLastTripsPanel,
    handleCloseLastTripsPanel,
    handleSelectLastTrip,
    lastTripsObjectName,
    handleAutoFitVisibleUnitsButtonClick,
    handleToggleSidePanelCollapse,
    handleSidePanelResizeMouseDown,
    handleSidePanelResizeKeyDown
  };
};

export default useDashboardMapPage;
